import { sql } from "drizzle-orm";
import { pgTable, text, uuid, timestamp, numeric, integer, boolean, jsonb, pgEnum } from "drizzle-orm/pg-core";
import { createInsertSchema, createSelectSchema } from "drizzle-zod";
import { z } from "zod";
import { deals } from "./schema-deals.js";
import { accounts } from "./schema-crm.js";
import { entityDocuments } from "./schema-documents.js";
import { dealConsolidatedSnapshot, dealServiceabilitySnapshots } from "./schema-deal-analysis.js";

// ============================================================================
// DEAL FINANCIAL ANALYSIS V2 MODULE - SPRINT 25
// ============================================================================
// Tables: 4 (deal_sensitivity_scenarios, deal_ebitda_adjustments,
//   deal_credit_memos, deal_credit_memo_sections)
// Purpose: Sensitivity analysis, deal-level EBITDA adjustments, credit memos
// ============================================================================

// ============================================================================
// ENUMS
// ============================================================================

export const sensitivityScenarioTypeEnum = pgEnum("crm_x_sensitivity_scenario_type_enum", [
  "rate_increase", "rate_decrease", "income_reduction", "income_increase", "combined", "stress_test"
]);

export const dealAdjustmentDirectionEnum = pgEnum("crm_x_deal_adjustment_direction_enum", [
  "add", "remove"
]);

export const dealAdjustmentTypeEnum = pgEnum("crm_x_deal_adjustment_type_enum", [
  "owner_salary", "related_rent", "non_recurring_income", "non_recurring_expense",
  "related_party_transaction", "personal_expense", "covid_impact", "custom"
]);

export const creditMemoStatusEnum = pgEnum("crm_x_credit_memo_status_enum", [
  "draft", "in_review", "approved", "submitted"
]);

export const creditMemoSectionEnum = pgEnum("crm_x_credit_memo_section_enum", [
  "entity_analysis", "guarantor_assessment", "security_position",
  "serviceability_analysis", "risk_factors", "recommendation"
]);

// ============================================================================
// TABLE 1: Deal Sensitivity Scenarios
// ============================================================================

export const dealSensitivityScenarios = pgTable("crm_x_deal_sensitivity_scenarios", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  dealId: uuid("deal_id").references(() => deals.id, { onDelete: "cascade" }).notNull(),
  scenarioName: text("scenario_name").notNull(),
  scenarioType: sensitivityScenarioTypeEnum("scenario_type").notNull(),
  // Rate parameters
  rateChangeBps: integer("rate_change_bps"),
  // Income parameters
  incomeChangePercent: numeric("income_change_percent", { precision: 5, scale: 2 }),
  affectedEntityId: uuid("affected_entity_id").references(() => accounts.id, { onDelete: "set null" }),
  // Results (stored after calculation)
  adjustedDscr: numeric("adjusted_dscr", { precision: 10, scale: 4 }),
  adjustedIcr: numeric("adjusted_icr", { precision: 10, scale: 4 }),
  adjustedSurplus: numeric("adjusted_surplus", { precision: 15, scale: 2 }),
  passesServiceability: boolean("passes_serviceability"),
  // Config
  isBaseCase: boolean("is_base_case").default(false),
  displayOrder: integer("display_order").default(0),
  notes: text("notes"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
  createdBy: uuid("created_by"),
});

export const insertDealSensitivityScenarioSchema = createInsertSchema(dealSensitivityScenarios, {
  dealId: z.string().uuid(),
  scenarioName: z.string().min(1),
  scenarioType: z.enum(["rate_increase", "rate_decrease", "income_reduction", "income_increase", "combined", "stress_test"]),
  rateChangeBps: z.number().int().nullable().optional(),
  incomeChangePercent: z.string().nullable().optional(),
  affectedEntityId: z.string().uuid().nullable().optional(),
  adjustedDscr: z.string().nullable().optional(),
  adjustedIcr: z.string().nullable().optional(),
  adjustedSurplus: z.string().nullable().optional(),
  passesServiceability: z.boolean().nullable().optional(),
  isBaseCase: z.boolean().default(false),
  displayOrder: z.number().int().default(0),
  notes: z.string().nullable().optional(),
}).omit({ id: true, createdAt: true, updatedAt: true, createdBy: true });

export const updateDealSensitivityScenarioSchema = insertDealSensitivityScenarioSchema.partial();
export const selectDealSensitivityScenarioSchema = createSelectSchema(dealSensitivityScenarios);
export type DealSensitivityScenario = typeof dealSensitivityScenarios.$inferSelect;
export type InsertDealSensitivityScenario = z.infer<typeof insertDealSensitivityScenarioSchema>;

// ============================================================================
// TABLE 2: Deal EBITDA Adjustments
// ============================================================================

export const dealEbitdaAdjustments = pgTable("crm_x_deal_ebitda_adjustments", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  dealId: uuid("deal_id").references(() => deals.id, { onDelete: "cascade" }).notNull(),
  periodLabel: text("period_label").notNull(),
  adjustmentType: dealAdjustmentTypeEnum("adjustment_type").notNull(),
  customLabel: text("custom_label"),
  description: text("description"),
  amount: numeric("amount", { precision: 15, scale: 2 }).notNull(),
  direction: dealAdjustmentDirectionEnum("direction").notNull(),
  supportingDocumentId: uuid("supporting_document_id").references(() => entityDocuments.id, { onDelete: "set null" }),
  notes: text("notes"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
  createdBy: uuid("created_by"),
});

export const insertDealEbitdaAdjustmentSchema = createInsertSchema(dealEbitdaAdjustments, {
  dealId: z.string().uuid(),
  periodLabel: z.string().min(1),
  adjustmentType: z.enum(["owner_salary", "related_rent", "non_recurring_income", "non_recurring_expense", "related_party_transaction", "personal_expense", "covid_impact", "custom"]),
  customLabel: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  amount: z.string(),
  direction: z.enum(["add", "remove"]),
  supportingDocumentId: z.string().uuid().nullable().optional(),
  notes: z.string().nullable().optional(),
}).omit({ id: true, createdAt: true, updatedAt: true, createdBy: true });

export const updateDealEbitdaAdjustmentSchema = insertDealEbitdaAdjustmentSchema.partial();
export const selectDealEbitdaAdjustmentSchema = createSelectSchema(dealEbitdaAdjustments);
export type DealEbitdaAdjustment = typeof dealEbitdaAdjustments.$inferSelect;
export type InsertDealEbitdaAdjustment = z.infer<typeof insertDealEbitdaAdjustmentSchema>;

// ============================================================================
// TABLE 3: Deal Credit Memos
// ============================================================================

export const dealCreditMemos = pgTable("crm_x_deal_credit_memos", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  dealId: uuid("deal_id").references(() => deals.id, { onDelete: "cascade" }).notNull(),
  organisationId: uuid("organisation_id").notNull(),
  title: text("title").notNull(),
  status: creditMemoStatusEnum("status").notNull().default("draft"),
  // Auto-populated summary fields (snapshot at creation)
  clientName: text("client_name"),
  dealReference: text("deal_reference"),
  totalFacilityAmount: numeric("total_facility_amount", { precision: 15, scale: 2 }),
  adjustedEbitda: numeric("adjusted_ebitda", { precision: 15, scale: 2 }),
  dscr: numeric("dscr", { precision: 10, scale: 4 }),
  icr: numeric("icr", { precision: 10, scale: 4 }),
  surplus: numeric("surplus", { precision: 15, scale: 2 }),
  passesServiceability: boolean("passes_serviceability"),
  // Metadata
  preparedBy: text("prepared_by"),
  reviewedBy: text("reviewed_by"),
  approvedAt: timestamp("approved_at", { withTimezone: true }),
  snapshotId: uuid("snapshot_id").references(() => dealConsolidatedSnapshot.id, { onDelete: "set null" }),
  version: integer("version").default(1),
  notes: text("notes"),
  lastPdfGeneratedAt: timestamp("last_pdf_generated_at", { withTimezone: true }),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
  createdBy: uuid("created_by"),
});

export const insertDealCreditMemoSchema = createInsertSchema(dealCreditMemos, {
  dealId: z.string().uuid(),
  title: z.string().min(1),
  status: z.enum(["draft", "in_review", "approved", "submitted"]).default("draft"),
  clientName: z.string().nullable().optional(),
  dealReference: z.string().nullable().optional(),
  totalFacilityAmount: z.string().nullable().optional(),
  adjustedEbitda: z.string().nullable().optional(),
  dscr: z.string().nullable().optional(),
  icr: z.string().nullable().optional(),
  surplus: z.string().nullable().optional(),
  passesServiceability: z.boolean().nullable().optional(),
  preparedBy: z.string().nullable().optional(),
  reviewedBy: z.string().nullable().optional(),
  approvedAt: z.string().nullable().optional(),
  snapshotId: z.string().uuid().nullable().optional(),
  version: z.number().int().default(1),
  notes: z.string().nullable().optional(),
  lastPdfGeneratedAt: z.string().nullable().optional(),
}).omit({ id: true, organisationId: true, createdAt: true, updatedAt: true, createdBy: true });

export const updateDealCreditMemoSchema = insertDealCreditMemoSchema.partial();
export const selectDealCreditMemoSchema = createSelectSchema(dealCreditMemos);
export type DealCreditMemo = typeof dealCreditMemos.$inferSelect;
export type InsertDealCreditMemo = z.infer<typeof insertDealCreditMemoSchema>;

// ============================================================================
// TABLE 4: Deal Credit Memo Sections
// ============================================================================

export const dealCreditMemoSections = pgTable("crm_x_deal_credit_memo_sections", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  creditMemoId: uuid("credit_memo_id").references(() => dealCreditMemos.id, { onDelete: "cascade" }).notNull(),
  organisationId: uuid("organisation_id").notNull(),
  sectionType: creditMemoSectionEnum("section_type").notNull(),
  sectionTitle: text("section_title").notNull(),
  content: text("content"),
  displayOrder: integer("display_order").default(0),
  autoPopulatedData: jsonb("auto_populated_data"),
  isComplete: boolean("is_complete").default(false),
  lastEditedAt: timestamp("last_edited_at", { withTimezone: true }),
  lastEditedBy: uuid("last_edited_by"),
  // GOLDEN-PATH F005: auto-population tracking columns (AC001)
  autoGenerated: boolean("auto_generated").notNull().default(false),
  autoGeneratedAt: timestamp("auto_generated_at", { withTimezone: true }),
  autoGeneratedSnapshotId: uuid("auto_generated_snapshot_id")
    .references(() => dealServiceabilitySnapshots.id),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
});

export const insertDealCreditMemoSectionSchema = createInsertSchema(dealCreditMemoSections, {
  creditMemoId: z.string().uuid(),
  sectionType: z.enum(["entity_analysis", "guarantor_assessment", "security_position", "serviceability_analysis", "risk_factors", "recommendation"]),
  sectionTitle: z.string().min(1),
  content: z.string().nullable().optional(),
  displayOrder: z.number().int().default(0),
  autoPopulatedData: z.record(z.unknown()).nullable().optional(),
  isComplete: z.boolean().default(false),
  lastEditedAt: z.string().nullable().optional(),
  lastEditedBy: z.string().uuid().nullable().optional(),
  // GOLDEN-PATH F005: auto-population tracking (AC001, AC008)
  autoGenerated: z.boolean().default(false).optional(),
  autoGeneratedAt: z.string().nullable().optional(),
  autoGeneratedSnapshotId: z.string().uuid().nullable().optional(),
}).omit({ id: true, organisationId: true, createdAt: true });

export const updateDealCreditMemoSectionSchema = insertDealCreditMemoSectionSchema.partial();
export const selectDealCreditMemoSectionSchema = createSelectSchema(dealCreditMemoSections);
export type DealCreditMemoSection = typeof dealCreditMemoSections.$inferSelect;
export type InsertDealCreditMemoSection = z.infer<typeof insertDealCreditMemoSectionSchema>;
